import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Clock, ArrowClockwise, ArrowUp,
  Trash, Warning, Check,
} from "@phosphor-icons/react";
import { api, brl, errMsg } from "@/lib/api";
import Paginator from "@/components/Paginator";

const StatusBadge = ({ s, ok }) => {
  if (s === "rejected") return <span className="text-xs text-[var(--danger)] flex items-center gap-1"><XCircle size={12} weight="fill" /> REJEITADO</span>;
  if (s === "approved" && ok) return <span className="text-xs text-[var(--success)] flex items-center gap-1"><CheckCircle size={12} weight="fill" /> ENVIADO</span>;
  if (s === "approved" && !ok) return <span className="text-xs text-[var(--accent)] flex items-center gap-1"><Clock size={12} weight="fill" /> VERIFICAR MP</span>;
  return <span className="text-xs text-white/60">{s?.toUpperCase()}</span>;
};

export default function AdminWithdraws() {
  const [data, setData] = useState({ withdraws: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/withdraws?page=${p}&per_page=10`);
      setData(data); setPage(p);
      setSelected(new Set());
    } catch { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(1); }, []);

  const approve = async (id) => {
    if (!window.confirm("Confirmar que o Pix foi enviado?")) return;
    await api.post(`/admin/withdraws/${id}/mark-sent`);
    toast.success("Saque marcado como enviado");
    load(page);
  };
  const reject = async (id) => {
    if (!window.confirm("Rejeitar e devolver saldo ao cliente?")) return;
    await api.post(`/admin/withdraws/${id}/reject`);
    toast.success("Saque rejeitado e valor devolvido");
    load(page);
  };

  const list = data.withdraws || [];

  const toggleSel = (id) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };
  const toggleSelAll = () => {
    const allSel = list.length > 0 && list.every((w) => selected.has(w.id));
    setSelected((prev) => {
      const nx = new Set(prev);
      if (allSel) list.forEach((w) => nx.delete(w.id));
      else list.forEach((w) => nx.add(w.id));
      return nx;
    });
  };

  const bulkDelete = async () => {
    setConfirmBulk(false);
    const ids = Array.from(selected);
    if (!ids.length) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.delete(`/admin/withdraws/${id}`); ok++; }
      catch { fail++; }
    }
    if (fail === 0) toast.success(`${ok} saque${ok === 1 ? "" : "s"} apagado${ok === 1 ? "" : "s"}`);
    else toast.warning(`${ok} apagado(s), ${fail} falharam`);
    load(page);
  };

  return (
    <div className="fadeUp">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="label-caps mb-2">Administração</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter flex items-center gap-3">
            <ArrowUp size={36} color="var(--accent)" weight="duotone" /> Saques
          </h1>
          <p className="text-white/60 mt-2">Todos os saques feitos pelos clientes. Aprovar/rejeitar apenas quando o Mercado Pago não confirmar automaticamente.</p>
        </div>
        <button onClick={() => load(page)} className="btn-ghost flex items-center gap-2" data-testid="refresh-withdraws"><ArrowClockwise size={14} /> Atualizar</button>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="mb-3 px-4 py-3 rounded-sm border border-[var(--accent)] bg-[var(--accent)]/10 flex items-center justify-between gap-3 fadeUp" data-testid="bulk-bar">
          <div className="text-sm"><b data-testid="bulk-count">{selected.size}</b> {selected.size === 1 ? "selecionado" : "selecionados"}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} data-testid="bulk-clear"
              className="text-[11px] px-3 py-1.5 rounded-sm border border-white/10 hover:bg-white/5">
              Limpar seleção
            </button>
            <button onClick={() => setConfirmBulk(true)} data-testid="bulk-delete"
              className="text-[11px] px-3 py-1.5 rounded-sm bg-[var(--danger)] text-white font-bold flex items-center gap-1.5 hover:brightness-110">
              <Trash size={11} weight="bold" /> Excluir selecionados
            </button>
          </div>
        </div>
      )}

      <div className="card-flat">
        <div className="p-4 border-b border-[var(--border)] grid grid-cols-12 gap-4 text-xs text-white/50 label-caps items-center">
          <div className="col-span-1">
            <input
              type="checkbox"
              checked={list.length > 0 && list.every((w) => selected.has(w.id))}
              onChange={toggleSelAll}
              data-testid="select-all-withdraws"
              className="accent-[var(--accent)] w-4 h-4 cursor-pointer"
              aria-label="Selecionar todos"
            />
          </div>
          <div className="col-span-3">Cliente</div>
          <div className="col-span-3">Chave Pix</div>
          <div className="col-span-2">Valor</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Ações</div>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {loading && <div className="p-6 text-white/50 text-sm">Carregando...</div>}
          {!loading && list.length === 0 && <div className="p-6 text-white/50 text-sm" data-testid="no-withdraws">Nenhum saque solicitado ainda.</div>}
          {list.map((w) => {
            const isSel = selected.has(w.id);
            return (
            <div key={w.id}
              className={`p-4 grid grid-cols-12 gap-4 items-center ${isSel ? "bg-[var(--accent)]/5" : "hover:bg-white/[0.02]"}`}
              data-testid={`withdraw-row-${w.id}`}>
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggleSel(w.id)}
                  data-testid={`select-${w.id}`}
                  className="accent-[var(--accent)] w-4 h-4 cursor-pointer"
                  aria-label="Selecionar"
                />
              </div>
              <div className="col-span-3">
                <div className="font-semibold text-sm">{w.from_name || w.user?.name}</div>
                <div className="text-xs text-white/50">{w.from_email || w.user?.email}</div>
                {w.user?.cpf_cnpj && <div className="text-xs text-white/40 font-mono">{w.user.cpf_cnpj}</div>}
              </div>
              <div className="col-span-3 text-xs font-mono truncate" title={w.pix_key}>
                {w.pix_key}
                {w.pix_key_type && <div className="text-[10px] text-white/40 uppercase mt-0.5">{w.pix_key_type}</div>}
              </div>
              <div className="col-span-2">
                <div className="font-display font-bold">{brl(w.amount)}</div>
                <div className="text-xs text-white/40">Taxa: {brl(w.fee)}</div>
              </div>
              <div className="col-span-1">
                <StatusBadge s={w.status} ok={w.payout_ok} />
                {w.payout_reason && <div className="text-[10px] text-[var(--danger)] mt-1 truncate" title={w.payout_reason}>{w.payout_reason.slice(0, 40)}</div>}
                <div className="text-[10px] text-white/40 mt-1">{new Date(w.created_at).toLocaleString("pt-BR")}</div>
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                {w.status !== "rejected" && !w.payout_ok && (
                  <>
                    <button onClick={() => approve(w.id)} className="btn-ghost text-xs px-2 py-1 text-[var(--success)]" data-testid={`approve-${w.id}`}>
                      <CheckCircle size={14} /> Enviado
                    </button>
                    <button onClick={() => reject(w.id)} className="btn-ghost text-xs px-2 py-1 text-[var(--danger)]" data-testid={`reject-${w.id}`}>
                      <XCircle size={14} /> Rejeitar
                    </button>
                  </>
                )}
              </div>
            </div>
          );})}
        </div>
        <Paginator page={page} pages={data.pages} onChange={load} total={data.total} perPage={10} />
      </div>

      {/* Modal Confirm Bulk */}
      {confirmBulk && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setConfirmBulk(false)}
          data-testid="confirm-modal"
        >
          <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-6 max-w-sm w-full fadeUp"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[var(--danger)]/20 flex items-center justify-center">
                <Warning size={18} weight="fill" color="var(--danger)" />
              </div>
              <div>
                <div className="font-display font-bold text-base">Excluir {selected.size} saque{selected.size === 1 ? "" : "s"}?</div>
                <div className="text-xs text-white/50">Ação irreversível</div>
              </div>
            </div>
            <p className="text-sm text-white/70 mb-5">
              Os registros serão apagados do histórico. Isso <b>não devolve</b> saldo — se algum saque estava aprovado, o valor permanece com o cliente.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmBulk(false)} data-testid="confirm-cancel"
                className="text-xs px-3 py-2 rounded-sm border border-white/10 hover:bg-white/5">
                Cancelar
              </button>
              <button onClick={bulkDelete} data-testid="confirm-delete"
                className="text-xs px-3 py-2 rounded-sm bg-[var(--danger)] text-white font-bold flex items-center gap-1.5">
                <Trash size={12} weight="bold" /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
